import { useEffect, useRef, useState } from 'react';
import { Pause, Play, RotateCcw, Timer } from 'lucide-react';
import { cn } from '@/lib/utils';

const PRESETS = [60, 90, 120, 180];

/**
 * Temporizador de descanso.
 *
 * Cuenta contra el reloj del sistema y no sumando intervalos: en móvil, con la
 * pantalla apagada, los temporizadores por intervalos se retrasan y acabas
 * descansando más de lo que crees.
 */
export function RestTimer() {
  const [target, setTarget] = useState(90);
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(90);
  const endsAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!running) return;

    const tick = () => {
      const endsAt = endsAtRef.current;
      if (endsAt == null) return;
      const left = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) setRunning(false);
    };

    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [running]);

  function start(seconds: number) {
    setTarget(seconds);
    setRemaining(seconds);
    endsAtRef.current = Date.now() + seconds * 1000;
    setRunning(true);
  }

  function toggle() {
    if (running) {
      setRunning(false);
      return;
    }
    endsAtRef.current = Date.now() + remaining * 1000;
    setRunning(true);
  }

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const done = remaining === 0;

  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--color-border)] p-3">
      <div className="flex items-center gap-2">
        <Timer aria-hidden="true" className="size-5 text-[var(--color-text-muted)]" />
        <output
          aria-live="off"
          className={cn(
            'flex-1 text-2xl font-bold tabular',
            done && 'text-[var(--color-brand-strong)]',
          )}
        >
          {minutes}:{String(seconds).padStart(2, '0')}
        </output>
        <button
          type="button"
          onClick={toggle}
          aria-label={running ? 'Pausar descanso' : 'Reanudar descanso'}
          className="tap w-14 rounded-xl border border-[var(--color-border)] text-[var(--color-text-muted)]"
        >
          {running ? (
            <Pause aria-hidden="true" className="mx-auto size-5" />
          ) : (
            <Play aria-hidden="true" className="mx-auto size-5" />
          )}
        </button>
        <button
          type="button"
          onClick={() => start(target)}
          aria-label="Reiniciar descanso"
          className="tap w-14 rounded-xl border border-[var(--color-border)] text-[var(--color-text-muted)]"
        >
          <RotateCcw aria-hidden="true" className="mx-auto size-5" />
        </button>
      </div>

      <div className="flex gap-1.5">
        {PRESETS.map((seconds) => (
          <button
            key={seconds}
            type="button"
            onClick={() => start(seconds)}
            className={cn(
              'tap flex-1 rounded-xl border text-sm font-medium tabular',
              target === seconds
                ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
            )}
          >
            {seconds < 120 ? `${seconds} s` : `${seconds / 60} min`}
          </button>
        ))}
      </div>
    </div>
  );
}
